import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Lock } from 'lucide-react';

const SharePage = () => {
  const { devotionalId } = useParams();
  const navigate = useNavigate();
  const [devotional, setDevotional] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDevotional();
  }, [devotionalId]);

  const loadDevotional = async () => {
    try {
      const response = await axios.get(`${API}/share/${devotionalId}`);
      setDevotional(response.data);
    } catch (error) {
      console.error('Failed to load devotional preview');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!devotional) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-2xl mb-4">Devotional not found</h2>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  const getSeasonClass = (season) => {
    const seasonMap = {
      'Lent': 'season-lent',
      'Advent': 'season-advent',
      'Christmas': 'season-christmas',
      'Epiphany': 'season-epiphany',
      'Holy Week': 'season-holy-week',
      'Easter': 'season-easter',
      'Ordinary Time': 'season-ordinary'
    };
    return seasonMap[season] || 'season-ordinary';
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <nav className="border-b border-primary/10 bg-background/80 backdrop-blur-lg">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <h1 className="text-2xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
            Rooted
          </h1>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="fade-in">
          {/* Season Badge */}
          <div className={`inline-flex items-center px-4 py-2 rounded-full mb-6 ${getSeasonClass(devotional.season)}`}
               style={{ backgroundColor: 'hsl(var(--season-color) / 0.1)', color: 'hsl(var(--season-color))' }}>
            <span className="text-sm font-medium">{devotional.season}</span>
          </div>

          {/* Liturgical Day */}
          <p className="text-sm text-muted-foreground mb-2">{devotional.date}</p>
          <h2 className="text-4xl mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            {devotional.liturgical_day}
          </h2>

          {/* Scripture */}
          <Card className="p-8 mb-8 border border-primary/10 shadow-sm">
            <p className="scripture-text text-muted-foreground">
              {devotional.scripture}
            </p>
          </Card>

          {/* Reflection Preview */}
          <section className="mb-8">
            <h3 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Reflection</h3>
            <p className="text-muted-foreground leading-relaxed whitespace-pre-line mb-4">
              {devotional.reflection_preview}
            </p>
            
            {/* Blur overlay effect for locked content */}
            <div className="relative">
              <div className="h-32 bg-gradient-to-b from-transparent via-background/80 to-background absolute inset-0 flex items-end justify-center pb-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Lock className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-sm">Subscribe to read full devotional</span>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Card */}
          <Card className="p-8 text-center border border-primary/10 bg-secondary/30">
            <h3 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Continue Your Spiritual Journey
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Join thousands experiencing daily devotionals, personal reflections, and spiritual growth through Rooted.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                className="rounded-full px-8"
                onClick={() => navigate('/subscribe')}
                data-testid="subscribe-cta-btn"
              >
                Subscribe Now
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8"
                onClick={() => navigate('/login')}
                data-testid="login-cta-btn"
              >
                Sign In
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-6">
              Starting at $2.50/month with annual plan
            </p>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-8 border-t mt-12">
        <div className="max-w-6xl mx-auto text-center text-muted-foreground">
          <p className="text-sm">&copy; 2025 Rooted. A space for contemplation and spiritual growth.</p>
        </div>
      </footer>
    </div>
  );
};

export default SharePage;
